import { useCallback, useEffect, useRef, useState } from 'react';

import { getCertificateWithInstallmentsById, getUserCertificatesAsync } from '@share/api';
import {
  CertificateStatusEnum,
  isInstalmentWalletPaymentType,
  type IUserCertificate,
} from '@share/common-types';
import {
  certificateHasFailedInstalmentPayment,
  instalmentScheduleHasFailedPayment,
} from '@share/utils';

interface UseOpenFailedInstalmentPaymentParams {
  email?: string;
  organizationId?: number;
  enabled?: boolean;
}

interface UseOpenFailedInstalmentPaymentResult {
  hasOpenFailedInstalmentPayment: boolean;
  isChecking: boolean;
  recheck: () => void;
}

const PENDING_STATUSES: CertificateStatusEnum[] = [CertificateStatusEnum.Pending];

async function resolveHasOpenFailedInstalmentPayment(
  certificates: IUserCertificate[],
): Promise<boolean> {
  const pendingInstalmentCertificates = certificates.filter(
    (certificate) =>
      certificate.status === CertificateStatusEnum.Pending &&
      isInstalmentWalletPaymentType(certificate.paymentType),
  );

  if (pendingInstalmentCertificates.some(certificateHasFailedInstalmentPayment)) {
    return true;
  }

  const certificatesWithoutSchedule = pendingInstalmentCertificates.filter(
    (certificate) => !certificate.instalmentSchedule?.instalments?.length,
  );

  if (certificatesWithoutSchedule.length === 0) {
    return false;
  }

  const installmentResponses = await Promise.all(
    certificatesWithoutSchedule.map(async (certificate) => {
      try {
        const { data } = await getCertificateWithInstallmentsById(certificate.id);
        return data.instalmentSchedule;
      } catch {
        return undefined;
      }
    }),
  );

  return installmentResponses.some(instalmentScheduleHasFailedPayment);
}

export function useOpenFailedInstalmentPayment(
  params: UseOpenFailedInstalmentPaymentParams = {},
): UseOpenFailedInstalmentPaymentResult {
  const { email, organizationId, enabled = true } = params;
  const [hasOpenFailedInstalmentPayment, setHasOpenFailedInstalmentPayment] =
    useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const requestIdRef = useRef<number>(0);

  const recheck = useCallback((): void => {
    if (!enabled || !email) {
      setHasOpenFailedInstalmentPayment(false);
      setIsChecking(false);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsChecking(true);

    void getUserCertificatesAsync({
      email,
      organizationId,
      statuses: PENDING_STATUSES,
      paging: { pageNumber: 1, pageSize: 100 },
    })
      .then(async ({ data }) => resolveHasOpenFailedInstalmentPayment(data.items ?? []))
      .then((hasFailedPayment) => {
        if (requestIdRef.current !== requestId) {
          return;
        }
        setHasOpenFailedInstalmentPayment(hasFailedPayment);
      })
      .catch(() => {
        if (requestIdRef.current !== requestId) {
          return;
        }
        setHasOpenFailedInstalmentPayment(false);
      })
      .finally(() => {
        if (requestIdRef.current !== requestId) {
          return;
        }
        setIsChecking(false);
      });
  }, [email, enabled, organizationId]);

  useEffect(() => {
    recheck();
  }, [recheck]);

  return {
    hasOpenFailedInstalmentPayment,
    isChecking,
    recheck,
  };
}
